import * as yup from 'yup';
import type {
  AcknowledgmentSettings,
  Businesses,
  IdentificationType,
  MedicalHistorySettings,
  StaticWaiverSettings,
  WaiverEmployeeSettings,
  WaiverEvents,
  WaiverIdentificationSettings,
  WaiverIdentificationSettingsTypes,
  WaiverOptionType,
  WaiverOptions,
  WaiverRequests,
  WaiverStatus,
  Waivers,
} from '@prisma/client';
import { SORTABLE_KEYS } from '../../constants/waivers';

export type WaiverSettingsType =
  | 'static'
  | 'acknowledgments'
  | 'medical'
  | 'identification'
  | 'employee'
  | 'address';

export interface StaticLiabilityWaiverValues {
  document: string | File | null;
  attestationText: string;
}

export interface IDUploadValues {
  types: IdentificationType[];
}

export interface AcknowledgmentValues {
  intro: string;
  options: WaiverOptions[];
}

export interface MedicalHistoryValues {
  intro: string;
  options: WaiverOptions[];
}

export interface EmployeeValues {
  options: WaiverOptions[];
}

export interface AcknowledgmentSettingsData extends AcknowledgmentSettings {
  options: WaiverOptions[];
}

export interface MedicalHistorySettingsData extends MedicalHistorySettings {
  options: WaiverOptions[];
}

export interface WaiverIdentificationSettingsData
  extends WaiverIdentificationSettings {
  types: WaiverIdentificationSettingsTypes[];
}

export interface WaiverEmployeeSettingsData extends WaiverEmployeeSettings {
  options: WaiverOptions[];
}

export interface GetWaiversData extends Waivers {
  acknowledgmentSettings: Omit<AcknowledgmentSettingsData, 'id'>;
  medicalHistorySettings: Omit<MedicalHistorySettingsData, 'id'>;
  staticWaiverSettings: Omit<StaticWaiverSettings, 'id'>;
  identificationSettings: Omit<WaiverIdentificationSettingsData, 'id'>;
  employeeSettings: Omit<WaiverEmployeeSettingsData, 'id'>;
}

export interface BusinessWaiverOptions {
  waiverName: string;
  waiverId: string;
  status: WaiverStatus;
  isDefault: boolean;
  services: {
    id: number;
    name: string;
  }[];
  hasChanges: boolean;
  publishedAt: string | null;
  acknowledgmentSettings: boolean;
  medicalHistorySettings: boolean;
  staticWaiverSettings: boolean;
  identificationSettings: boolean;
  employeeSettings: boolean;
}

export interface GetWaiversResponse {
  options: BusinessWaiverOptions[];
  draft: GetWaiversData;
  published: GetWaiversData;
}

export interface WaiverRequestWithCustomerAndEvents extends WaiverRequests {
  customer: {
    user: {
      firstName: string;
      lastName: string;
    };
  };
  events: WaiverEvents[];
  appointment: {
    startDateTime: string;
    onschedId: string;
    appointmentId: string;
  };
}

export interface GetWaiverRequestsResponse {
  waiverRequests: WaiverRequestWithCustomerAndEvents[];
  totalCount: number;
}

export interface GetWaiverRequestResponse {
  waiverRequest: WaiverRequests;
  waiver: GetWaiversData & {
    business: Pick<Businesses, 'businessId'>;
  };
}

export interface PatchWaiverRequestPayload {
  businessId: string;
  waiverRequestId: string;
}

export interface PublishWaiversResponse extends Waivers {}

export const baseSettingsSchema = yup.object({
  enabled: yup.boolean().optional(),
});

export const staticWaiverSettingsSchema = baseSettingsSchema.concat(
  yup.object({
    documentLink: yup.string().nullable().optional(),
    attestationText: yup
      .string()
      .min(1, "Attestation text can't be empty")
      .optional(),
  }),
);

const ids = ['ID', 'BIRTH_CERTIFICATE', 'GUARDIAN_ID'] as const;
export const identificationSettingsSchema = baseSettingsSchema.concat(
  yup.object({
    types: yup
      .array(
        yup.object({
          type: yup
            .mixed<IdentificationType>()
            .oneOf(ids, 'Please select a valid ID type')
            .required(),
          enabled: yup.boolean().required(),
          text: yup.string().optional(),
        }),
      )
      .length(ids.length, 'Invalid types selected')
      .test(
        'at-least-one-enabled',
        'At least one type must be enabled',
        types => !types || types.some(type => type.enabled),
      )
      .optional(),
  }),
);

export const enabledIdentificationTypesSchema = yup.object({
  types: yup
    .array(
      yup
        .mixed<IdentificationType>()
        .oneOf(ids, 'Please select a valid ID type'),
    )
    .min(1, 'At least one type must be enabled'),
});

export const waiverOption = yup.object({
  id: yup.string().required(),
  type: yup
    .mixed<WaiverOptionType>()
    .oneOf(['CHECKBOX', 'TEXT', 'MULTILINE_TEXT', 'SIGNATURE'])
    .required(),
  text: yup
    .string()
    .test('required', 'Text is required', value => !!value?.trim())
    .required(),
  order: yup.number().required(),
});

export const acknowledgmentSettingsSchema = baseSettingsSchema.concat(
  yup.object({
    intro: yup.string().optional(),
    options: yup.array(waiverOption).optional(),
  }),
);

export const medicalHistorySettingsSchema = baseSettingsSchema.concat(
  yup.object({
    intro: yup.string().optional(),
    options: yup.array(waiverOption).optional(),
  }),
);

export const employeeSettingsSchema = baseSettingsSchema.concat(
  yup.object({
    options: yup.array(waiverOption).optional(),
  }),
);

export const waiverNameSchema = yup
  .string()
  .transform(value => (typeof value === 'string' ? value.trim() : value))
  .min(1, 'Name is required');

export const getUpdateWaiverNameSchema = (currentNames: string[]) =>
  yup.object({
    name: waiverNameSchema
      .notOneOf(currentNames, 'Waiver with this name already exists')
      .required(),
  });

export const updateWaiverSchema = yup.object({
  businessId: yup.string().required(),
  staticWaiverSettings: staticWaiverSettingsSchema.optional(),
  identificationSettings: identificationSettingsSchema.optional(),
  acknowledgmentSettings: acknowledgmentSettingsSchema.optional(),
  medicalHistorySettings: medicalHistorySettingsSchema.optional(),
  employeeSettings: employeeSettingsSchema.optional(),
  collectAddress: yup.boolean().optional(),
  update: yup.boolean().optional(),
  name: waiverNameSchema.optional(),
});

export const createWaiverSchema = yup.object({
  businessId: yup.string().required(),
  name: waiverNameSchema.required(),
  serviceIds: yup
    .array(yup.number().integer().required())
    .nullable()
    .optional(),
});

export const getEmployeeWaiverQuestionsSchema = (options: WaiverOptions[]) =>
  yup.object(
    options.reduce(
      (schema, option) => {
        // eslint-disable-next-line no-param-reassign
        schema[option.id] =
          option.type === 'TEXT' || option.type === 'MULTILINE_TEXT'
            ? yup.string().notRequired()
            : option.type === 'SIGNATURE'
            ? yup.string().min(1, 'Signature is required').required()
            : yup.boolean().required();
        return schema;
      },
      {} as Record<string, yup.AnySchema>,
    ),
  );

export type UpdateWaiverPayload = yup.InferType<typeof updateWaiverSchema>;

export type CreateWaiverPayload = yup.InferType<typeof createWaiverSchema>;

export type WaiverOption = yup.InferType<typeof waiverOption>;

export interface UpdateWaiverResponse extends PublishWaiversResponse {}

export interface CreateWaiverResponse extends GetWaiversData {}

export type SortableKeys = (typeof SORTABLE_KEYS)[number];

export type EmployeeWaiverQuestions = yup.InferType<
  ReturnType<typeof getEmployeeWaiverQuestionsSchema>
>;

export type SubmitEmployeeWaiverPayload = {
  waiverRequestId: string;
  answers: EmployeeWaiverQuestions;
  signedFileLink: string;
};

export type WaiverResponseData = {
  customerDetails: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    dob: string;
    address?: {
      streetAddress: string;
      streetAddress2: string | undefined;
      city: string;
      state: string;
      zip: string;
    };
  };
  acknowledgments: Record<string, boolean>;
  medicalHistory: Record<string, boolean | string>;
  employee?: Record<string, boolean | string>;
};
